import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { CampaignSummary } from "../../lib/api";
import { useNavigate } from "react-router-dom";

interface CampaignBreakdownChartProps {
  campaigns: CampaignSummary[];
}

export default function CampaignBreakdownChart({
  campaigns,
}: CampaignBreakdownChartProps) {
  const navigate = useNavigate();

  if (campaigns.length === 0) {
    return (
      <div className="card p-6">
        <h2 className="font-semibold text-gray-800 mb-4">Campaign breakdown</h2>
        <p className="text-sm text-gray-400">No campaigns with posts yet.</p>
      </div>
    );
  }

  // Truncate long campaign names for the chart
  const data = campaigns.slice(0, 10).map((c) => ({
    ...c,
    shortName: c.name.length > 20 ? c.name.slice(0, 18) + "..." : c.name,
    pending: c.total - c.approved - c.rejected,
  }));

  return (
    <div className="card p-6">
      <h2 className="font-semibold text-gray-800 mb-4">Campaign breakdown</h2>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={data}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(state: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const id = (state?.activePayload as any[])?.[0]?.payload
              ?.campaignId;
            if (id) navigate(`/analytics/campaigns/${id}`);
          }}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis
            dataKey="shortName"
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            tickLine={false}
            axisLine={{ stroke: "#E5E7EB" }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid #E5E7EB",
              fontSize: 12,
            }}
            labelFormatter={(_label, payload) => {
              const p = payload as unknown as { payload?: { name?: string } }[];
              return p?.[0]?.payload?.name ?? String(_label);
            }}
          />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar
            dataKey="published"
            name="Published"
            stackId="a"
            fill="#3B82F6"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="approved"
            name="Approved"
            stackId="a"
            fill="#10B981"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="rejected"
            name="Rejected"
            stackId="a"
            fill="#EF4444"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="pending"
            name="Pending"
            stackId="a"
            fill="#D1D5DB"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-xs text-gray-400 mt-2">
        Click a campaign bar for detailed breakdown.
      </p>
    </div>
  );
}
